import 'css/src/components/common/dashboard-sidebar';

import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { RoleEnum, Urls } from 'js/src/client';
import { DashboardProjectList } from 'js/src/components/common/DashboardProjectList';
import HeaderLogo from 'js/src/components/common/HeaderLogo';
import WorkspaceSelector from 'js/src/components/common/WorkspaceSelector';
import { getIsUserAllowedAccessToAccessBlueprintsAndBrandKits } from 'js/src/data/account';
import {
    ALL_VIDEOS_VIEW_ID,
    resetSearchedVideos,
    selectActiveView,
    setActiveView,
    TRASH_VIDEOS_VIEW_ID,
} from 'js/src/data/dashboard';
import { setLastAccessedProject } from 'js/src/data/media/cookie-operations';
import { MODAL_IDS, showModal } from 'js/src/data/modals';
import { COLOR_L5_GREY_700, COLOR_L5_PRIMARY_500, roleCanDeleteVideos } from 'js/src/libs/consts';
import BlueprintsIcon from 'js/src/libs/custom_icons/BlueprintsIcon';
import Navigation from 'js/src/libs/navigation';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { hasFeatureEnabled } from 'js/src/libs/server-utils';

const IS_ON_DASHBOARD =
    typeof SERVER !== 'undefined' && window.location.pathname === Urls.DASHBOARD;

/**
 * The DashboardSidebar is used to frame all "dashboard" pages. That includes
 * pages like the dashboard (which lists the user's videos), the instant videos page, etc.
 * Sometimes luminary is available (on the dashboard), sometimes not.
 */

type DashboardSidebarPropsFromRedux = ConnectedProps<typeof connector>;

const DashboardSidebarPresentation = React.memo(function DashboardSidebarPresentation({
    activeView,
    userCanAccessBlueprintsAndBrandKits,
    showModal,
    setActiveView,
    resetSearchedVideos,
}: DashboardSidebarPropsFromRedux) {
    const handleProjectCreation = React.useCallback(() => {
        showModal({ modal_id: MODAL_IDS.PROJECT_CREATE });
    }, [showModal]);

    const setActiveViewInternal = React.useCallback(
        (newView: number) => {
            setActiveView({
                view: newView,
            });
        },
        [setActiveView],
    );

    return (
        <div className="dashboard-sidebar">
            <div className="overflow-container overflow-y-auto">
                <div className="m-l5-16 ms-l5-24 align-logo">
                    <HeaderLogo />
                </div>
                <div className="mx-l5-8">
                    <WorkspaceSelector />
                </div>
                <ul className="navigation navigation-main navigation-accordion">
                    <NavigationList
                        activeView={activeView}
                        setActiveView={setActiveViewInternal}
                        resetSearchedVideos={resetSearchedVideos}
                        userCanAccessBlueprintsAndBrandKits={userCanAccessBlueprintsAndBrandKits}
                    />
                    <br />
                    {IS_ON_DASHBOARD && (
                        <DashboardProjectList onProjectAdd={handleProjectCreation} />
                    )}
                </ul>
            </div>
        </div>
    );
});

const mapStateToProps = (state: RootState) => {
    return {
        activeView: IS_ON_DASHBOARD ? selectActiveView(state) : undefined,
        userCanAccessBlueprintsAndBrandKits:
            getIsUserAllowedAccessToAccessBlueprintsAndBrandKits(state),
    };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
    return bindActionCreators(
        {
            showModal,
            setActiveView,
            resetSearchedVideos,
        },
        dispatch,
    );
};

const connector = connect(mapStateToProps, mapDispatchToProps);
const DashboardSidebar = connector(DashboardSidebarPresentation);

export default DashboardSidebar;

/*******************************
 * PRESENTATION HELPERS
 ******************************/

/**
 * Renders list items for navigating to video related urls
 */
type NavigationListProps = {
    activeView?: number;
    setActiveView: (activeView: number) => void;
    resetSearchedVideos: VoidFunction;
    userCanAccessBlueprintsAndBrandKits: boolean;
};

const NavigationList: React.FC<NavigationListProps> = React.memo(function NavigationList({
    activeView,
    setActiveView,
    resetSearchedVideos,
    userCanAccessBlueprintsAndBrandKits,
}) {
    const currUrl = window.location.pathname;
    const isAllVideosNavActive = activeView === ALL_VIDEOS_VIEW_ID;
    const isTrashNavActive = activeView === TRASH_VIDEOS_VIEW_ID;

    const showAllVideos = React.useCallback(() => {
        if (!IS_ON_DASHBOARD) {
            Navigation.goToLocation(Urls.DASHBOARD);
        }
        setActiveView(ALL_VIDEOS_VIEW_ID);
        resetSearchedVideos();
        setLastAccessedProject(null, SERVER.USER.workspace_id);
    }, [setActiveView, resetSearchedVideos]);

    const showTrashVideos = React.useCallback(() => {
        if (!IS_ON_DASHBOARD) {
            throw new Error('showTrashVideos should only be called on the dashboard');
        }
        setActiveView(TRASH_VIDEOS_VIEW_ID);
        resetSearchedVideos();
    }, [resetSearchedVideos, setActiveView]);

    const canAccessRSSFeeds =
        SERVER.USER.role === RoleEnum.ADMIN || SERVER.USER.role === RoleEnum.EDITOR;
    const canAccessTrash = IS_ON_DASHBOARD && roleCanDeleteVideos(SERVER.USER.role);

    return (
        <>
            <li className={isAllVideosNavActive ? 'active' : ''} id="dashboard-videos">
                <a onClick={showAllVideos}>
                    <i className="material-symbols-outlined">ondemand_video</i>
                    <span>All videos</span>
                </a>
            </li>
            {userCanAccessBlueprintsAndBrandKits && (
                <li className={currUrl === Urls.BRAND_KITS ? 'active' : ''} id="brand-kits">
                    <a href={Urls.BRAND_KITS}>
                        <i className="material-symbols-outlined">palette</i>
                        <span>Brand kits</span>
                    </a>
                </li>
            )}
            {userCanAccessBlueprintsAndBrandKits && (
                <>
                    <li
                        className={currUrl === Urls.BLUEPRINTS_PAGE ? 'active' : ''}
                        id="blueprints"
                    >
                        <a href={Urls.BLUEPRINTS_PAGE}>
                            <>
                                <i className="material-symbols-outlined">
                                    <BlueprintsIcon
                                        fillColor={
                                            currUrl === Urls.BLUEPRINTS_PAGE
                                                ? COLOR_L5_PRIMARY_500
                                                : COLOR_L5_GREY_700
                                        }
                                    />
                                </i>
                                <span>Blueprints</span>
                            </>
                        </a>
                    </li>
                </>
            )}
            {SERVER.USER.role === RoleEnum.ADMIN && (
                <li className={currUrl === Urls.ANALYTICS ? 'active' : ''}>
                    <a href={Urls.ANALYTICS}>
                        <i className="material-symbols-outlined">insert_chart</i>
                        <span>Analytics</span>
                    </a>
                </li>
            )}
            {canAccessRSSFeeds && (
                <li className={currUrl === Urls.RSS_FEEDS ? 'active' : ''} id="rss-feeds">
                    <a href={Urls.RSS_FEEDS}>
                        <i className="material-symbols-outlined">rss_feed</i>
                        <span>RSS feeds</span>
                    </a>
                </li>
            )}
            {hasFeatureEnabled('animated_explainer_video') && (
                <>
                    <li
                        className={currUrl === Urls.ANIMATED_EXPLAINER_VIDEOS ? 'active' : ''}
                        id="animated-explainer-videos"
                    >
                        <a href={Urls.ANIMATED_EXPLAINER_VIDEOS}>
                            <i className="material-symbols-outlined">movie</i>
                            <span>GEN-X</span>
                        </a>
                    </li>
                    <li
                        className={currUrl === '/genx-preset-builder/' ? 'active' : ''}
                        id="genx-preset-builder"
                    >
                        <a href="/genx-preset-builder/">
                            <i className="material-symbols-outlined">tune</i>
                            <span>Preset Builder</span>
                        </a>
                    </li>
                    <li className={currUrl === '/genx-evals/' ? 'active' : ''} id="genx-evals">
                        <a href="/genx-evals/">
                            <i className="material-symbols-outlined">lab_research</i>
                            <span>Preset Evals</span>
                        </a>
                    </li>
                </>
            )}
            {canAccessTrash && (
                <li className={isTrashNavActive ? 'active' : ''} id="trash-videos">
                    <a onClick={showTrashVideos}>
                        <i className="material-symbols-outlined">delete</i>
                        <span>Trash</span>
                    </a>
                </li>
            )}
        </>
    );
});
